import { Plus } from "lucide-react";
import { AdminPageShell } from "@/components/shared/admin-page-shell";
import { Button } from "@/components/ui/button";
import { Routes } from "@/constants/enums";
import { appPath } from "@/constants/paths";
import { ObsScreenCard } from "@/features/obs/components/ObsScreenCard";
import {
  useCanManageObs,
  useObsReports,
  useObsScreens,
} from "@/features/obs/hooks/useObsQueries";

/**
 * تقارير الموازنة المفتوحة — replace the data behind the six budget screens on
 * the public site.
 *
 * These screens replaced Power BI embeds that Microsoft retired on 2026-08-31,
 * so this page is the ONLY way their figures get updated from here on. Gated on
 * `obs:manage` (SUPER_ADMIN-only by baseline) because an upload rewrites
 * national budget figures on a public site.
 *
 * New reports are created here too, from a workbook whose structure nobody has
 * described — the profiler proposes a column mapping, the operator confirms it,
 * and the confirmed mapping is saved so every later upload for that report is
 * validated exactly the way the six are.
 */
export default function ObsReportsPage() {
  const canManage = useCanManageObs();
  const { data, isLoading, error } = useObsScreens(canManage);
  // The report rows carry the mapping and chart list; the dataset rows carry the
  // version history. Paired by slug so a card can offer both.
  const { data: reports } = useObsReports(canManage);
  const reportBySlug = new Map((reports ?? []).map((r) => [r.slug, r]));

  return (
    <AdminPageShell
      title="تقارير الموازنة المفتوحة"
      description="تحديث بيانات الشاشات المنشورة على الموقع عبر رفع ملفات Excel."
      isLoading={isLoading}
      error={error as Error | null}
    >
      {!canManage ? (
        <div className="rounded-lg border border-border bg-muted/30 p-6 text-center text-sm text-muted-foreground">
          لا تملك صلاحية إدارة تقارير الموازنة المفتوحة.
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="rounded-lg border border-border bg-muted/30 p-4 text-sm text-muted-foreground">
              كل رفع يُنشئ إصدارًا جديدًا ولا يحذف السابق، ويُفحص الملف قبل نشره
              — فإن كان سيُفرغ إحدى الشاشات فسيُرفض ولن يتغيّر شيء على الموقع.
            </div>
            {/*
              Opens in ITS OWN TAB, and with a plain <a> rather than a router
              <Link> — a new tab needs a real navigation, and the wizard is a
              six-step job with a file upload in the middle of it. Leaving this
              page intact means an operator can still check an existing report's
              history, or start a second one, without losing where they were.

              `rel="noopener"` because the wizard tab has no business holding a
              handle on this one.
            */}
            <Button asChild>
              <a
                href={appPath(Routes.OBS_REPORTS, "new")}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Plus className="size-4" />
                تقرير جديد
              </a>
            </Button>
          </div>

          {(data ?? []).map((screen) => (
            <ObsScreenCard
              key={screen.slug}
              screen={screen}
              report={reportBySlug.get(screen.slug)}
            />
          ))}
        </div>
      )}
    </AdminPageShell>
  );
}
